import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Api } from '@core/api';
import { injectTSDI } from '@core/tsdi';
import { Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { AddFacilityRestrictionDialog } from './add-facility-restriction-dialog';
import {
    FacilityRestrictionDto,
    FacilityRestrictionCreateRequestDto
} from '@api/ChallengeClient';

const CommunityFacilityRestrictions = () => {
    const { communityId } = useParams();
    const { challengeClient } = injectTSDI(Api);

    const [dialogOpen, setDialogOpen] = React.useState(false);

    const parsedCommunityId = parseInt(communityId);

    const restrictionsRequest = useWrapRequest<FacilityRestrictionDto[], any>(
        async () => {
            const response =
                await challengeClient.support.communityFacilityRestrictionSupportControllerGetRestrictions(
                    parsedCommunityId
                );
            return response.data;
        },
        {
            deps: [communityId]
        }
    );

    const removeRestriction = async (restrictionId: number) => {
        await challengeClient.support.communityFacilityRestrictionSupportControllerRemoveRestriction(
            parsedCommunityId,
            restrictionId
        );
        restrictionsRequest.request([communityId]);
    };

    const addRestriction = async (facilityId: number, facilityName: string) => {
        const body: FacilityRestrictionCreateRequestDto = {
            facilityId,
            facilityName
        };
        await challengeClient.support.communityFacilityRestrictionSupportControllerAddRestriction(
            parsedCommunityId,
            body
        );
        setDialogOpen(false);
        restrictionsRequest.request([communityId]);
    };

    return restrictionsRequest.match({
        loading: () => (
            <div className="flex min-h-[200px] items-center justify-center">
                <Spinner />
            </div>
        ),
        error: () => (
            <Alert variant="destructive">
                <AlertDescription>
                    Failed to load facility restrictions.
                </AlertDescription>
            </Alert>
        ),
        fetched: (restrictions) => (
            <>
                <div className="mb-4 flex items-center justify-between gap-4">
                    <p className="text-sm font-medium">Facility Restrictions</p>
                    <Button size="sm" onClick={() => setDialogOpen(true)}>
                        <Plus />
                        Add Restriction
                    </Button>
                </div>

                <Card>
                    <CardContent className="p-0">
                        {restrictions.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                No facility restrictions configured for this
                                community.
                            </p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Facility ID</TableHead>
                                        <TableHead>Facility Name</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead className="text-right">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {restrictions.map((restriction) => (
                                        <TableRow key={restriction.id}>
                                            <TableCell>
                                                {restriction.facilityId}
                                            </TableCell>
                                            <TableCell>
                                                {restriction.facilityName}
                                            </TableCell>
                                            <TableCell>
                                                {restriction.type}
                                            </TableCell>
                                            <TableCell>
                                                {new Date(
                                                    restriction.createdDate
                                                ).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon-sm"
                                                    className="text-destructive"
                                                    onClick={() =>
                                                        void removeRestriction(
                                                            restriction.id
                                                        )
                                                    }
                                                >
                                                    <Trash2 />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                <AddFacilityRestrictionDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onAdd={addRestriction}
                />
            </>
        )
    });
};

export default CommunityFacilityRestrictions;
